import { styled, StyletronComponent } from 'styletron-react';
import { Theme } from '../../Themes/themeProvider/ThemeProvider';
import { focusRing, focusRingNoGap } from '../../Themes/base/focusRing';
import { borderColor, borderRadius, borderStyle, borderWidth } from '../../Utilities/utils/Theming';

export const MonthYearSelectIconContainer: StyletronComponent<any> = styled('div', {
	height: '20px',
});

export const WeekdayHeaderStyles = ($theme: typeof Theme) => {
	return {
		color: 'var(--epods--color-text-primary)',
		fontSize: 'var(--epods--font-size-p)',
		fontFamily: 'var(--epods--font-base)',
		width: $theme.calendar.dayWidth,
		height: 'var(--epods--line-height-p)',
		marginBottom: $theme.calendar.weekPaddingBottom,
		paddingTop: '0',
		paddingBottom: '0',
		lineHeight: 'var(--epods--line-height-p)',
		backgroundColor: 'var(--epods--color-bg-heading)',
	};
};

const getDisabledColorIfNeedIt = (disabled: boolean) => disabled ? 'var(--epods--color-text-disabled)' : 'var(--epods--color-text-primary)'

export const DayStyles = ({ $theme, $isHighlighted, $selected, $isHovered, $outsideMonth, $date, $disabled }: any) => {
	const today = new Date(new Date().setHours(0, 0, 0, 0)).toISOString();
	const isToday = $date.toISOString() === today;

	// Note: highlighted === isHovered
	const bgColor = $selected
		? {
			backgroundColor: 'var(--epods--color-bg-ds)',
			borderTopWidth: '1px',
			borderRightWidth: '1px',
			borderBottomWidth: '1px',
			borderLeftWidth: '1px',
			...borderStyle('solid'),
			borderTopColor: 'var(--epods--color-cyan-a-400)',
			borderRightColor: 'var(--epods--color-cyan-a-400)',
			borderBottomColor: 'var(--epods--color-cyan-a-400)',
			borderLeftColor: 'var(--epods--color-cyan-a-400)',
			outline: '1px solid var(--epods--color-cyan-a-400)',
		}
		: $isHovered
			? {
				backgroundColor: 'transparent',
				borderTopWidth: '1px',
				borderRightWidth: '1px',
				borderBottomWidth: '1px',
				borderLeftWidth: '1px',
				...borderStyle('solid'),
				borderTopColor: 'var(--epods--color-text-primary)',
				borderRightColor: 'var(--epods--color-text-primary)',
				borderBottomColor: 'var(--epods--color-text-primary)',
				borderLeftColor: 'var(--epods--color-text-primary)',
			}
			: {};

	const rangeBackgroundColor = {
		backgroundColor: 'var(--epods--color-bg-visited)',
	};

	return {
		width: $theme.calendar.dayWidth,
		height: $theme.calendar.dayHeight,
		lineHeight: $theme.calendar.dayHeight,
		fontSize: 'var(--epods--font-size-p)',
		paddingBottom: '0',
		paddingTop: '0',
		marginTop: $theme.calendar.marginTop,
		marginBottom: $theme.calendar.marginBottom,
		fontFamily: 'var(--epods--font-base)',
		color: $selected ? 'var(--epods--color-text-inverted)' : getDisabledColorIfNeedIt($disabled),
		':before': {
			...rangeBackgroundColor,
		},
		':after': {
			...borderRadius('var(--epods--border-radius)'),
			boxShadow: 'none',
			...(isToday
				? borderColor('var(--epods--color-border-active) !important')
				: ($selected && !($isHovered || $isHighlighted)) || $outsideMonth
					? borderColor('transparent')
					: borderColor('var(--epods--color-bg-primary)')),
			...bgColor,
		},
		':last-child:before': {
			borderBottomRightRadius: 'var(--epods--border-radius) !important',
			borderTopRightRadius: 'var(--epods--border-radius) !important',
		},
		':first-child:before': {
			borderBottomLeftRadius: 'var(--epods--border-radius) !important',
			borderTopLeftRadius: 'var(--epods--border-radius) !important',
		},
	};
};

export const DatepickerSpacingGlobal = (theme: typeof Theme) => {
	return {
		paddingLeft: theme.calendar.containerPaddingHorizontal,
		paddingRight: theme.calendar.containerPaddingHorizontal,
		paddingTop: theme.calendar.containerPaddingTop,
		paddingBottom: theme.calendar.containerPaddingBottom,
	};
};

export const DatepickerSpacingMonthSelector = (theme: typeof Theme) => {
	return {
		paddingLeft: 'var(--epods--space-inset-m)',
		paddingRight: 'var(--epods--space-inset-m)',
		paddingTop: theme.calendar.containerPaddingTop,
		paddingBottom: theme.calendar.containerPaddingBottom,
		backgroundColor: 'var(--epods--color-bg-heading)',
	};
};

export const DatepickerSpacingWeek = (theme: typeof Theme) => {
	return {
		paddingBottom: '0',
		marginTop: '0',
		marginBottom: '0',
		paddingTop: '0',
	};
};

export const DatepickerMonthSelectorOption = ({ $isHighlighted }: any) => {
	const valueBorderColor = $isHighlighted ? 'var(--epods--color-text-primary)' : 'transparent';

	return {
		...borderWidth('1px'),
		...borderColor(valueBorderColor),
		...borderStyle('solid'),
		...borderRadius('var(--epods--border-radius)'),
		marginLeft: '4px',
		marginRight: '4px',
		color: 'var(--epods--color-text-primary)',
		fontFamily: 'var(--epods--font-base)',
		fontSize: '16px',
		lineHeight: '24px',
		paddingTop: '5px',
		paddingBottom: '5px',
		backgroundColor: 'var(--epods--color-bg-third-elevation)',
		fontWeight: '100',
		':hover': {
			borderTopWidth: '1px',
			borderRightWidth: '1px',
			borderBottomWidth: '1px',
			borderLeftWidth: '1px',
			...borderStyle('solid'),
			borderTopColor: 'var(--epods--color-text-primary)',
			borderRightColor: 'var(--epods--color-text-primary)',
			borderBottomColor: 'var(--epods--color-text-primary)',
			borderLeftColor: 'var(--epods--color-text-primary)',
			backgroundColor: 'var(--epods--color-bg-third-elevation) ',
			color: 'var(--epods--color-text-primary)',
		},
	}
};
